import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AssetAppeal, Roles } from "types";
import { errorHandler, hasRole } from "utils/helper";
import { ITEMS_PER_PAGE } from "utils/constant";
import PageHeader from "components/PageHeader";
import {
  getAssetAppealsByAdminUnit,
  getAssetAppealsByProsecutor,
} from "services/assetAppealService";

const AssetReferal: React.FC = () => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [activeTab, setActiveTab] = useState("All");
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [assetAppeals, setAssetAppeals] = useState<AssetAppeal[]>([]);
  const navigate = useNavigate();

  const isAssetCoordinator = hasRole(loginData?.roles, [
    Roles.assetCoordinator,
  ]);
  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);
  const isAssetRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isDirector = hasRole(loginData?.roles, [Roles.director]);

  // Filter states
  const [selectedDate, setSelectedDate] = useState("");

  useEffect(() => {
    setLoading(true);
    if (isAssetProsecutor) {
      getAssetAppealsByProsecutor(loginData?.user_id.toString() as string)
        .then((data) => {
          setAssetAppeals(data);
          setLoading(false);
        })
        .catch((error) => {
          showTooltip(errorHandler(error));
          setLoading(false);
        });
    } else {
      getAssetAppealsByAdminUnit(loginData?.admin_unit.toString() as string)
        .then((data) => {
          setAssetAppeals(data);
          setLoading(false);
        })
        .catch((error) => {
          showTooltip(errorHandler(error));
          setLoading(false);
        });
    }
  }, [
    isAssetProsecutor,
    isAssetRegistrar,
    loginData?.admin_unit,
    loginData?.org_unit,
  ]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const paginatedAppeals = assetAppeals?.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(assetAppeals?.length / ITEMS_PER_PAGE);

  const handleDateFilter = (date: string) => {
    setSelectedDate(date);
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const tabs = [t("all")];

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title={t("prosecutor_asset_appeals")}
        icon={FaBriefcase}
        tabs={tabs}
        activeTab={activeTab}
        onTabClick={handleTabClick}
        rightContent={
          <>
            {(isAssetProsecutor || isAssetRegistrar || isDirector) && (
              <div className="flex items-end gap-4">
                {/* Date Filter */}
                <div className="flex flex-col">
                  <label className="text-xs font-medium text-gray-600 mb-1">
                    {t("filter_by_date")}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      className="px-3 py-2 text-sm border rounded w-48"
                      value={selectedDate}
                      onChange={(e) => handleDateFilter(e.target.value)}
                    />
                    {selectedDate && (
                      <button
                        onClick={() => setSelectedDate("")}
                        className="px-2 py-1 text-xs text-gray-600 bg-gray-200 rounded hover:bg-gray-300"
                        title={t("clear_date_filter")}
                      >
                        {t("clear")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        }
      />

      <div className="overflow-hidden bg-white shadow rounded-xl">
        {loading ? (
          <Loading label={t("loading_appeals")} />
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead className="text-white bg-black">
              <tr>
                <th colSpan={7} className="p-4 rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                      <button
                        onClick={() => navigate(-1)}
                        className="text-white hover:text-gray-300"
                        title={t("go_back")}
                      >
                        {t("back")}
                      </button>
                      <span>{t("list_table")}</span>
                    </div>

                    <div className="relative w-80">
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("search_asset_appeals")}
                        className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                      />
                    </div>
                  </div>
                </th>
              </tr>
              <tr>
                <th className="p-3 text-left">{t("referral_ref_no")}</th>
                <th className="p-3 text-left">{t("registered_by")}</th>
                <th className="p-3 text-left">{t("status")}</th>
                <th className="p-3 text-left">{t("appeal_type")}</th>
                <th className="p-3 text-left">{t("date_submitted")}</th>
                <th className="p-3 text-left rounded-tr-lg">{t("action")}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedAppeals.map((appeal, idx) => (
                <tr
                  key={idx + indexOfFirstItem}
                  className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                >
                  <td className="p-3">{appeal?.asset_referral?.referral_id}</td>
                  <td className="p-3 font-[500] font-[Poppins]">
                    {appeal?.requesting_prosecutor_name || t("na")}
                  </td>
                  <td className="p-3 font-[500] font-[Poppins]">
                    <p className="bg-gray-100 rounded text-gray-800 p-2 w-fit">
                      {appeal?.action_status}
                    </p>
                  </td>
                  <td className="p-3 font-[500] font-[Poppins]">
                    {appeal?.appeal_type}
                  </td>
                  <td className="p-3 font-[500] font-[Poppins]">
                    {appeal?.date_submitted}
                  </td>
                  <td className="p-3 font-[500] font-[Poppins]">
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`/asset-appeal/${appeal.id}`);
                      }}
                      className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                    >
                      {t("view")}
                    </button>
                  </td>
                </tr>
              ))}
              {assetAppeals?.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-gray-500">
                    {assetAppeals.length === 0
                      ? t("no_asset_appeals_found")
                      : t("no_asset_appeals_match_search")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            {t("prev")}
          </button>
          <span className="px-2">
            {t("page_of", { currentPage, totalPages })}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            {t("next")}
          </button>
        </div>
      )}
    </div>
  );
};

export default AssetReferal;
